/*
 * File: MessageCreatorModal.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 2, 2025 at 9:51 AM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	memo, useEffect, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import validator from 'validator';
import { useDispatch, useSelector } from 'react-redux';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslation, Trans } from 'react-i18next';
import { useWindowResize } from 'beautiful-react-hooks';

// Utilities
import { toastMessage, toastSuccess } from '../../utilities/toaster';
import { containsUrl, timeout } from '../../../utilities/utilities';

// Services
import { submitMessage, validateMessage } from '../../services/message';
import { getCurrentUser } from '../../services/user';

// Store
import { updateUser } from '../../store/slices/user/user.slice';

// Components
import { Modal } from '../Modal';
import { Typography } from '../Typography';
import { Button } from '../Button';
import { TextArea } from '../TextArea';
import { IconElement } from '../IconElement';
import { EmailAccountInput } from '../EmailAccountInput';

// Constants
import { AVAILABLE_I18N_NAMESPACES } from '../../../constants/base.constants';

// Styles
import * as S from './MessageCreatorModal.styles';


/**
 * Component
 */

export const MessageCreatorModal = memo(({
	isOpen, handleClose, postCreateActions
}) => {

	// Create state
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [message, setMessage] = useState(null);
	const [email, setEmail] = useState(null);
	const [isEmailLocked, setIsEmailLocked] = useState(false);
	const [currentStep, setCurrentStep] = useState(1);
	const [emailStepHeight, setEmailStepHeight] = useState(190);
	const [alertText, setAlertText] = useState(null);
	const [alertHeight, setAlertHeight] = useState(40);
	const [showAlert, setShowAlert] = useState(false);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Create references
	const inputRef = useRef();
	const alertContainerRef = useRef();
	const emailStepRef = useRef();

	// Get current UI mode from hook
	const uiMode = useSelector((state) => state.ui.value);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Get hooks
	const dispatch = useDispatch();

	// Get actions from hooks
	const { executeRecaptcha } = useGoogleReCaptcha();

	// Handle validate message
	const validateMessageContent = async (validateEmail = true) => {

		// Validate parameters
		if (!message || validator.isEmpty(message, { ignore_whitespace: true })) {

			// Show error
			toastMessage(uiMode, partner, t('messageCreatorModal.missingMessageError'));

			// Return
			return false;
		}
		if (validateEmail && (!email || !validator.isEmail(email))) {

			// Show error
			toastMessage(uiMode, partner, t('messageCreatorModal.missingEmailError'));

			// Return
			return false;
		}

		// Check message content for web links
		if (containsUrl(message, [
			'www.ourwave.org',
			'stories.ourwave.org',
			'community.ourwave.org',
			'discover.ourwave.org',
			partner.full_domain
		].filter(Boolean))) {

			// Show alert
			setAlertText(t('messageCreatorModal.webLinksError', { partnerName: partner.name }));
			setShowAlert(true);

			// Return
			return false;
		}

		// Update submission state
		setIsSubmitting(true);

		// Update alert state
		setShowAlert(false);

		// Pre-review message
		const { data: { result: { validation_error: validationError } } } = await validateMessage({ content: message });

		// Show error if necessary
		if (validationError) {

			// Show alert
			setAlertText(t(`validationErrors.${validationError}_message`, { partnerName: partner.name }));
			setShowAlert(true);

			// Update submission state
			setIsSubmitting(false);

			// Return
			return false;
		}

		// Return true
		return true;
	};

	// Handle validate submission
	const validateSubmission = async (e) => {

		// Prevent default
		e.preventDefault();

		// Validate message
		const result = await validateMessageContent(false);

		// Return if necessary
		if (!result) return;

		// Attempt to fetch user
		try {

			// Fetch current user
			const { data: { result: { user } } } = await getCurrentUser();

			// Update current user
			dispatch(updateUser(user));

			// Update user state
			setEmail(user.email && user.email_verified ? user.email : null);
			setIsEmailLocked(user.email && user.email_verified);

		} catch (_) {}

		// Set UI timeout
		await timeout(1000);

		// Update submission state
		setIsSubmitting(false);

		// Update current step
		setCurrentStep(2);
	};

	// Handle post message
	const postMessage = async (e) => {

		// Prevent default
		e.preventDefault();

		// Validate message
		const result = await validateMessageContent(true);

		// Return if necessary
		if (!result) return;

		// Update alert state
		setShowAlert(false);

		// Create parameters
		const params = { content: message, submissionEmail: email };

		// Add reCAPTCHA token
		try { params.recaptchaToken = await executeRecaptcha('submit_support_message'); } catch (_) {}

		// Attempt message submission
		try {

			// Post message
			const { data: { result: { message: messageObj } } } = await submitMessage(params);

			// Update submission state
			setIsSubmitting(false);

			// Clear inputs
			setMessage(null);
			setEmail(null);

			// Show success message
			toastSuccess(uiMode, partner, t('messageCreatorModal.confirmationMessage', { partnerName: partner.name }));

			// Handle post create actions
			postCreateActions(messageObj);

			// Close modal
			setTimeout(() => {
				handleClose();
			}, 2000);

		} catch (error) {

			// Update submission state
			setIsSubmitting(false);

			// Get parameters
			const isValidationError = error?.response?.data?.error?.is_validation_error;

			// Check if validation error
			if (isValidationError) {

				// Get validation error
				const validationError = error?.response?.data?.error?.validation_error;

				// Show alert
				setAlertText(t(`validationErrors.${validationError}_message`, { partnerName: partner.name }));
				setShowAlert(true);

			} else {

				// Show error
				toastMessage(uiMode, partner, t('messageCreatorModal.submissionError'));
			}
		}
	};

	// Handle on input change action
	const handleOnChange = (event) => {
		const { name, value } = event.target;
		if (name === 'message') {
			setMessage(value);
		} else if (name === 'email') {
			setEmail(value);
		}
	};

	// Handle actions on window resize
	useWindowResize(() => {

		// Set element heights
		setAlertHeight(alertContainerRef.current.clientHeight);
		setEmailStepHeight(emailStepRef.current.clientHeight);
	});

	// Handle actions on alert text change
	useEffect(() => {

		// Set element height
		setAlertHeight(alertContainerRef.current.clientHeight);

	}, [alertText]);

	// Handle actions on component open
	useEffect(() => {
		if (isOpen) {

			// Reset inputs
			setMessage(null);
			setEmail(null);

			// Set timeout
			setTimeout(() => {

				// Focus input
				inputRef.current?.focus();

			}, 100);

		} else {

			// Set timeout
			setTimeout(() => {

				// Reset step
				setCurrentStep(1);

			}, 400);
		}

		// Reset alert
		setShowAlert(false);

		// Set element height
		setAlertHeight(alertContainerRef.current.clientHeight);
		setEmailStepHeight(emailStepRef.current.clientHeight);

	}, [isOpen]);

	// Render component
	return (
		<Modal isOpen={isOpen} showOverlay handleClose={handleClose} clickOutsideClose={false}>
			<S.ModalInner>
				<S.Form>

					{/* Message Step */}
					<S.MessageStepContainer>

						{/* Content */}
						<Typography tag="h3" weight="black">
							<Trans t={t} i18nKey="messageCreatorModal.title">
								Post a Message
							</Trans>
						</Typography>
						<Typography tag="p" variation="2">
							<Trans t={t} i18nKey="messageCreatorModal.description">
								Share a message of support with the community.
							</Trans>
						</Typography>

						{/* Inputs */}
						<S.InputContainer>
							<TextArea
								placeholder={t('messageCreatorModal.messagePlaceholder')}
								name="message"
								type="text"
								rows={7}
								characterLimit={500}
								defaultHideCharacterLimit
								value={message || ''}
								onChange={handleOnChange}
								smartField
								ref={inputRef}
							/>
						</S.InputContainer>

						{/* Alert Container */}
						<S.AlertContainer className={showAlert ? 'animate show' : 'animate'} ref={alertContainerRef} $height={alertHeight}>
							<IconElement icon={['fad', 'triangle-exclamation']} />
							<Typography tag="p" variation="3" weight="medium">{alertText}</Typography>
						</S.AlertContainer>

						{/* Actions */}
						{currentStep === 1 && (
							<Button disabled={isSubmitting} isLoading={isSubmitting} onClick={validateSubmission}>
								<Trans t={t} i18nKey="actions.continue">
									Continue
								</Trans>
							</Button>
						)}

					</S.MessageStepContainer>

					{/* Email Step */}
					<S.EmailStepContainer className={currentStep === 2 ? 'animate emailStep show' : 'emailStep animate'} ref={emailStepRef} $height={emailStepHeight}>

						{/* Email Prompt */}
						<S.EmailPromptContainer>
							<IconElement icon={['fad', 'envelope-dot']} />
							<Typography tag="p" variation="2" weight="regular">
								<Trans t={t} i18nKey="messageCreatorModal.emailPrompt">
									We will send you an email as soon as your question is answered, as well as send helpful resources and support.
								</Trans>
							</Typography>
						</S.EmailPromptContainer>

						{/* Account Email */}
						<EmailAccountInput
							placeholder={t('messageCreatorModal.emailPlaceholder')}
							email={email}
							isLocked={isEmailLocked}
							handleOnChange={handleOnChange}
							unlockField={() => {
								setEmail(null);
								setIsEmailLocked(false);
							}}
						/>

						{/* Email Step - Actions */}
						<Button disabled={isSubmitting} isLoading={isSubmitting} onClick={postMessage}>
							<Trans t={t} i18nKey="actions.post">
								Post
							</Trans>
						</Button>

					</S.EmailStepContainer>

				</S.Form>

				{/* Legal Snippet */}
				<S.LegalSnippet context="message" />

			</S.ModalInner>
		</Modal>
	);
});


/**
 * Configuration
 */

MessageCreatorModal.displayName = 'MessageCreatorModal';
MessageCreatorModal.propTypes = {
	isOpen: PropTypes.bool,
	handleClose: PropTypes.func,
	postCreateActions: PropTypes.func,
};
MessageCreatorModal.defaultProps = {
	isOpen: false,
	postCreateActions: null,
	handleClose: null,
};
